import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Generate unique dancer properties based on name
const generateDancerFromName = (name) => {
  // Create a simple hash from the name
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = ((hash << 5) - hash) + name.charCodeAt(i);
    hash = hash & hash;
  }
  hash = Math.abs(hash);

  // Generate unique properties
  const colors = ['#ffde59', '#ff6b6b', '#4ecdc4', '#a855f7', '#22d3ee', '#f97316', '#84cc16'];
  const poses = ['arms-up', 'arms-out', 'one-arm', 'wave', 'jump'];
  const speeds = [0.4, 0.5, 0.6, 0.7, 0.8];

  return {
    color: colors[hash % colors.length],
    pose: poses[(hash >> 4) % poses.length],
    speed: speeds[(hash >> 8) % speeds.length],
    size: 28 + (hash % 12), // 28-40px
    offsetX: (hash % 20) - 10, // -10 to 10
    offsetY: (hash % 16) - 8, // -8 to 8
  };
};

// Personalized Dancer SVG with animation frames based on pose
const PersonalizedDancer = ({ config, className, frame = 1 }) => {
  const { pose, color } = config;

  // Each pose has 2 frames for animation
  const poses = {
    'arms-up': {
      1: (
        <svg width="24" height="32" viewBox="0 0 24 32" fill={color} className={className}>
          <rect x="9" y="0" width="6" height="6" />
          <rect x="9" y="7" width="6" height="10" />
          <rect x="3" y="4" width="6" height="3" />
          <rect x="0" y="1" width="3" height="6" />
          <rect x="15" y="4" width="6" height="3" />
          <rect x="21" y="1" width="3" height="6" />
          <rect x="9" y="18" width="3" height="8" />
          <rect x="6" y="26" width="3" height="6" />
          <rect x="12" y="18" width="3" height="8" />
          <rect x="15" y="26" width="3" height="6" />
        </svg>
      ),
      2: (
        <svg width="24" height="32" viewBox="0 0 24 32" fill={color} className={className}>
          <rect x="9" y="0" width="6" height="6" />
          <rect x="9" y="7" width="6" height="10" />
          <rect x="3" y="5" width="6" height="3" />
          <rect x="0" y="2" width="3" height="6" />
          <rect x="15" y="5" width="6" height="3" />
          <rect x="21" y="2" width="3" height="6" />
          <rect x="6" y="18" width="3" height="8" />
          <rect x="3" y="26" width="3" height="6" />
          <rect x="15" y="18" width="3" height="8" />
          <rect x="18" y="26" width="3" height="6" />
        </svg>
      ),
    },
    'arms-out': {
      1: (
        <svg width="28" height="32" viewBox="0 0 28 32" fill={color} className={className}>
          <rect x="11" y="0" width="6" height="6" />
          <rect x="11" y="7" width="6" height="10" />
          <rect x="0" y="9" width="11" height="3" />
          <rect x="17" y="9" width="11" height="3" />
          <rect x="11" y="18" width="3" height="8" />
          <rect x="8" y="26" width="3" height="6" />
          <rect x="14" y="18" width="3" height="8" />
          <rect x="17" y="26" width="3" height="6" />
        </svg>
      ),
      2: (
        <svg width="28" height="32" viewBox="0 0 28 32" fill={color} className={className}>
          <rect x="11" y="0" width="6" height="6" />
          <rect x="11" y="7" width="6" height="10" />
          <rect x="0" y="7" width="11" height="3" />
          <rect x="17" y="11" width="11" height="3" />
          <rect x="8" y="18" width="3" height="8" />
          <rect x="5" y="26" width="3" height="6" />
          <rect x="17" y="18" width="3" height="8" />
          <rect x="20" y="26" width="3" height="6" />
        </svg>
      ),
    },
    'one-arm': {
      1: (
        <svg width="24" height="32" viewBox="0 0 24 32" fill={color} className={className}>
          <rect x="9" y="0" width="6" height="6" />
          <rect x="9" y="7" width="6" height="10" />
          <rect x="3" y="10" width="6" height="3" />
          <rect x="0" y="13" width="3" height="4" />
          <rect x="15" y="4" width="6" height="3" />
          <rect x="21" y="1" width="3" height="6" />
          <rect x="9" y="18" width="3" height="8" />
          <rect x="6" y="26" width="3" height="6" />
          <rect x="12" y="18" width="3" height="8" />
          <rect x="15" y="26" width="3" height="6" />
        </svg>
      ),
      2: (
        <svg width="24" height="32" viewBox="0 0 24 32" fill={color} className={className}>
          <rect x="9" y="0" width="6" height="6" />
          <rect x="9" y="7" width="6" height="10" />
          <rect x="3" y="8" width="6" height="3" />
          <rect x="0" y="11" width="3" height="4" />
          <rect x="15" y="6" width="6" height="3" />
          <rect x="21" y="3" width="3" height="6" />
          <rect x="6" y="18" width="3" height="8" />
          <rect x="3" y="26" width="3" height="6" />
          <rect x="15" y="18" width="3" height="8" />
          <rect x="18" y="26" width="3" height="6" />
        </svg>
      ),
    },
    'wave': {
      1: (
        <svg width="24" height="32" viewBox="0 0 24 32" fill={color} className={className}>
          <rect x="9" y="0" width="6" height="6" />
          <rect x="9" y="7" width="6" height="10" />
          <rect x="3" y="7" width="6" height="3" />
          <rect x="0" y="4" width="3" height="6" />
          <rect x="15" y="7" width="6" height="3" />
          <rect x="21" y="4" width="3" height="3" />
          <rect x="21" y="10" width="3" height="3" />
          <rect x="9" y="18" width="3" height="8" />
          <rect x="6" y="26" width="3" height="6" />
          <rect x="12" y="18" width="3" height="8" />
          <rect x="15" y="26" width="3" height="6" />
        </svg>
      ),
      2: (
        <svg width="24" height="32" viewBox="0 0 24 32" fill={color} className={className}>
          <rect x="9" y="0" width="6" height="6" />
          <rect x="9" y="7" width="6" height="10" />
          <rect x="3" y="9" width="6" height="3" />
          <rect x="0" y="6" width="3" height="6" />
          <rect x="15" y="5" width="6" height="3" />
          <rect x="21" y="2" width="3" height="3" />
          <rect x="21" y="8" width="3" height="3" />
          <rect x="6" y="18" width="3" height="8" />
          <rect x="3" y="26" width="3" height="6" />
          <rect x="15" y="18" width="3" height="8" />
          <rect x="18" y="26" width="3" height="6" />
        </svg>
      ),
    },
    'jump': {
      1: (
        <svg width="24" height="32" viewBox="0 0 24 32" fill={color} className={className}>
          <rect x="9" y="0" width="6" height="6" />
          <rect x="9" y="7" width="6" height="10" />
          <rect x="3" y="5" width="6" height="3" />
          <rect x="0" y="2" width="3" height="6" />
          <rect x="15" y="5" width="6" height="3" />
          <rect x="21" y="2" width="3" height="6" />
          <rect x="6" y="18" width="3" height="6" />
          <rect x="3" y="24" width="3" height="8" />
          <rect x="15" y="18" width="3" height="6" />
          <rect x="18" y="24" width="3" height="8" />
        </svg>
      ),
      2: (
        <svg width="24" height="32" viewBox="0 0 24 32" fill={color} className={className}>
          <rect x="9" y="2" width="6" height="6" />
          <rect x="9" y="9" width="6" height="10" />
          <rect x="3" y="7" width="6" height="3" />
          <rect x="0" y="4" width="3" height="6" />
          <rect x="15" y="7" width="6" height="3" />
          <rect x="21" y="4" width="3" height="6" />
          <rect x="9" y="20" width="3" height="6" />
          <rect x="6" y="26" width="3" height="6" />
          <rect x="12" y="20" width="3" height="6" />
          <rect x="15" y="26" width="3" height="6" />
        </svg>
      ),
    },
  };

  return poses[pose]?.[frame] || poses['arms-up'][frame];
};

// Animated preview dancer for the modal
const AnimatedPreviewDancer = ({ config }) => {
  const [frame, setFrame] = useState(1);

  useEffect(() => {
    const interval = setInterval(() => {
      setFrame(prev => prev === 1 ? 2 : 1);
    }, 350);
    return () => clearInterval(interval);
  }, []);

  return (
    <div style={{ width: 70, height: 90, margin: '0 auto' }}>
      <PersonalizedDancer config={config} className="w-full h-full" frame={frame} />
    </div>
  );
};

export const NameModal = ({ onComplete }) => {
  const [name, setName] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedDancer, setGeneratedDancer] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsGenerating(true);
    
    // Generate the dancer
    setTimeout(() => {
      const dancer = generateDancerFromName(name.trim());
      setGeneratedDancer({ ...dancer, name: name.trim() });
      setIsGenerating(false);
      setShowPreview(true);
    }, 1200);
  };

  const handleContinue = () => {
    // Save to localStorage
    localStorage.setItem('aya_user_dancer', JSON.stringify(generatedDancer));
    localStorage.setItem('aya_user_name', generatedDancer.name);
    onComplete(generatedDancer);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] bg-black flex items-center justify-center p-4"
      data-testid="name-modal"
    >
      <div className="max-w-md w-full">
        <AnimatePresence mode="wait">
          {!showPreview ? (
            <motion.div
              key="input"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="text-center"
            >
              {/* Dancing icon */}
              <motion.div
                animate={{ rotate: [0, -10, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
                className="text-6xl mb-8"
              >
                <svg width="64" height="80" viewBox="0 0 24 32" fill="#ffde59" className="mx-auto">
                  <rect x="9" y="0" width="6" height="6" />
                  <rect x="9" y="7" width="6" height="10" />
                  <rect x="3" y="7" width="6" height="3" />
                  <rect x="0" y="4" width="3" height="6" />
                  <rect x="15" y="7" width="6" height="3" />
                  <rect x="21" y="4" width="3" height="6" />
                  <rect x="9" y="18" width="3" height="8" />
                  <rect x="6" y="26" width="3" height="6" />
                  <rect x="12" y="18" width="3" height="8" />
                  <rect x="15" y="26" width="3" height="6" />
                </svg>
              </motion.div>

              {/* Heading */}
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-3">
                Before we groove...
              </h2>
              <p className="text-zinc-400 text-base md:text-lg mb-8">
                Drop your name & I'll create your signature dance move ✨
              </p>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  maxLength={20}
                  autoFocus
                  data-testid="name-input"
                  className="
                    w-full px-0 py-4 text-center
                    bg-transparent border-0 border-b-2 border-white/20
                    text-white text-2xl font-heading placeholder-zinc-600
                    focus:outline-none focus:border-[#ffde59]
                    transition-colors duration-300
                  "
                />

                <motion.button
                  type="submit"
                  disabled={!name.trim() || isGenerating}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="generate-dancer-btn"
                  className="
                    w-full py-4 bg-[#ffde59] text-black
                    font-heading font-bold text-lg
                    disabled:opacity-50 disabled:cursor-not-allowed
                    transition-all duration-300
                  "
                >
                  {isGenerating ? (
                    <span className="flex items-center justify-center gap-2">
                      <motion.span
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="inline-block w-5 h-5 border-2 border-black border-t-transparent rounded-full"
                      />
                      Choreographing...
                    </span>
                  ) : (
                    "Make my move"
                  )}
                </motion.button>
              </form>

              {/* Skip option */}
              <button
                onClick={() => onComplete(null)}
                className="mt-6 text-zinc-600 text-sm hover:text-zinc-400 transition-colors"
                data-testid="skip-name-btn"
              >
                Skip for now
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="preview"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center"
            >
              {/* Generated Dancer Preview - Animated */}
              <div className="mb-8">
                <AnimatedPreviewDancer config={generatedDancer} />
              </div>

              <h2 className="font-heading font-bold text-2xl md:text-3xl text-white mb-2">
                Meet your dancer, <span style={{ color: generatedDancer.color }}>{generatedDancer.name}</span>!
              </h2>
              <p className="text-zinc-400 text-base mb-8">
                Your unique move is ready. Let's hit the floor.
              </p>

              <motion.button
                onClick={handleContinue}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="continue-to-site-btn"
                className="
                  w-full py-4 bg-[#ffde59] text-black
                  font-heading font-bold text-lg
                  transition-all duration-300
                "
              >
                Let's go →
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// Export the personalized dancer component for use in the main site
export const UserDancer = ({ position = 'pos-1', delay = 0 }) => {
  const [config, setConfig] = useState(null);
  const [frame, setFrame] = useState(1);
  const [randomOffset] = useState(() => ({
    x: Math.random() * 15 - 7,
    y: Math.random() * 20 - 10,
  }));

  useEffect(() => {
    const saved = localStorage.getItem('aya_user_dancer');
    if (saved) {
      setConfig(JSON.parse(saved));
    }
  }, []);

  // Animation loop - toggle between frames
  useEffect(() => {
    if (!config) return;
    const interval = setInterval(() => {
      setFrame(prev => prev === 1 ? 2 : 1);
    }, 400 + (config.speed * 300));

    return () => clearInterval(interval);
  }, [config]);

  if (!config) return null;

  // Desktop positions - scattered around hero
  // Mobile positions - only safe spots (top corners, away from content)
  const positionConfig = {
    'pos-1': { 
      desktop: { top: '15%', left: '5%' },
      mobile: { top: '12%', left: '5%' },
      showOnMobile: true 
    },
    'pos-2': { 
      desktop: { top: '20%', right: '6%' },
      mobile: { bottom: '18%', right: '5%' },
      showOnMobile: true 
    },
    'pos-3': { 
      desktop: { top: '55%', left: '8%' },
      mobile: null,
      showOnMobile: false 
    },
    'pos-4': { 
      desktop: { top: '60%', right: '5%' },
      mobile: null,
      showOnMobile: false 
    },
  };

  const posConfig = positionConfig[position] || positionConfig['pos-1'];

  return (
    <>
      {/* Desktop version */}
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 0.6, scale: 1 }}
        transition={{ delay: delay / 1000, duration: 0.6 }}
        className="absolute z-10 pointer-events-none hidden md:block"
        style={{ 
          ...posConfig.desktop,
          width: 24,
          height: 32,
          transform: `translate(${randomOffset.x}px, ${randomOffset.y}px)`,
        }}
        title={`${config.name}'s dancer`}
      >
        <PersonalizedDancer config={config} className="w-full h-full" frame={frame} />
      </motion.div>
      
      {/* Mobile version - only for safe positions */}
      {posConfig.showOnMobile && posConfig.mobile && (
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 0.5, scale: 1 }}
          transition={{ delay: delay / 1000, duration: 0.6 }}
          className="absolute z-10 pointer-events-none md:hidden"
          style={{ 
            ...posConfig.mobile,
            width: 20,
            height: 26,
          }}
          title={`${config.name}'s dancer`}
        >
          <PersonalizedDancer config={config} className="w-full h-full" frame={frame} />
        </motion.div>
      )}
    </>
  );
};

export { generateDancerFromName, PersonalizedDancer };
export default NameModal;
